import { useNavigate } from '@tanstack/react-router'
import { ArrowUp, LoaderCircle } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@react-spectrum/s2'
import { setHomeChatHandoff } from '@client/chat-handoff'
import { trackAnalyticsEvent } from '@client/analytics'
import { QuickPromptCards } from '@/components/QuickPromptCards'
import { cn } from '@/lib/utils'

type HomeChatEntryProps = {
  initialQuickPrompts: Array<string>
}

export function HomeChatEntry({ initialQuickPrompts }: HomeChatEntryProps) {
  const navigate = useNavigate()
  const [quickPrompts] = useState(initialQuickPrompts)
  const [input, setInput] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const canSubmit = !isNavigating && input.trim().length > 0

  const syncTextareaHeight = () => {
    const inputElement = inputRef.current
    if (!inputElement) return
    inputElement.style.height = '0px'
    inputElement.style.height = `${Math.min(inputElement.scrollHeight, 240)}px`
  }

  useEffect(() => {
    syncTextareaHeight()
  }, [input, isExpanded])

  const handleSubmit = () => {
    if (isNavigating) return

    const query = input.trim()
    if (!query) return

    trackAnalyticsEvent('chat_entry_submitted', {
      entry_point: 'home',
      source: 'composer',
    })

    setIsNavigating(true)
    setHomeChatHandoff(query)

    const goToChat = async () => {
      try {
        await navigate({
          to: '/chat',
          search: {
            source: 'home',
          },
        })
      } catch (error) {
        console.error('Failed to open chat from home:', error)
        setIsNavigating(false)
      }
    }

    const maybeStartViewTransition: unknown = Reflect.get(
      document,
      'startViewTransition',
    )
    if (typeof maybeStartViewTransition === 'function') {
      maybeStartViewTransition.call(document, () => {
        void goToChat()
      })
      return
    }

    void goToChat()
  }

  const handleQuickPromptSelect = (prompt: string) => {
    if (isNavigating) return
    trackAnalyticsEvent('home_quick_prompt_selected', {
      prompt_index: quickPrompts.indexOf(prompt),
    })
    setInput(prompt)
    setIsExpanded(true)
    requestAnimationFrame(() => {
      syncTextareaHeight()
    })
  }

  return (
    <div className="w-full max-w-3xl space-y-5 [view-transition-name:home-chat-entry]">
      <div className="relative">
        <div className="pointer-events-none absolute -inset-2 rounded-[1.6rem] bg-gradient-to-r from-primary/25 via-card to-primary/15 blur-xl" />
        <div
          className={cn(
            'relative rounded-[1.35rem] border border-border bg-card px-4 py-3 shadow-[0_14px_44px_-20px_rgba(23,34,52,0.35)] backdrop-blur-sm transition-all duration-300',
            isExpanded &&
              'border-ring/60 shadow-[0_20px_52px_-20px_rgba(23,34,52,0.45)]',
            isNavigating && 'border-ring/60 bg-secondary/30',
          )}
        >
          <textarea
            id="home-chat-entry-input"
            name="message"
            aria-label="Ask a building regulations question"
            placeholder="Ask a question..."
            value={input}
            rows={isExpanded ? 4 : 3}
            ref={inputRef}
            autoComplete="off"
            spellCheck="false"
            onFocus={() => setIsExpanded(true)}
            onBlur={() => {
              if (!input.trim()) setIsExpanded(false)
            }}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                handleSubmit()
              }
            }}
            disabled={isNavigating}
            className={cn(
              'block min-h-[3.75rem] w-full max-h-[240px] resize-none overflow-y-auto border-0 bg-transparent px-0 py-0 pb-11 text-[18px] leading-7 text-foreground outline-none placeholder:text-muted-foreground/90',
              isNavigating && 'cursor-not-allowed text-muted-foreground',
            )}
          />
          <div
            data-testid="home-chat-submit-wrap"
            className="absolute bottom-3 right-3 z-10"
          >
            <Button
              type="button"
              size="S"
              variant="primary"
              onPress={() => handleSubmit()}
              isDisabled={isNavigating || !canSubmit}
              className="relative z-10 rounded-full"
              aria-label={isNavigating ? 'Starting chat' : 'Send message'}
            >
              {isNavigating ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
              <span className="sr-only">
                {isNavigating ? 'Starting...' : 'Send'}
              </span>
            </Button>
          </div>
        </div>
        {isNavigating ? (
          <p
            className="mt-2 text-center text-sm font-medium text-muted-foreground"
            role="status"
          >
            Opening chat...
          </p>
        ) : null}
      </div>

      <QuickPromptCards
        prompts={quickPrompts}
        onSelect={handleQuickPromptSelect}
        testId="home-quick-prompt"
      />
    </div>
  )
}
